/**
 * Attendance — the module's page shell.
 *
 * ── One screen per route, and no tab strip ────────────────────────────────────────────────
 *
 * The sidebar already lists every attendance screen. A tab strip repeating that list is the
 * same navigation twice: two places to click for the same destination, two things to keep in
 * step when a screen is added, and two answers to "where am I". So this matches ShiftRosterPage
 * — the URL decides which screen is shown, the heading names it, and only that screen renders.
 *
 * ── Who may record manual attendance is configured in SETTINGS, not here ──────────────────
 *
 * Settings › Attendance Settings owns that screen, next to User Management and Organisation
 * Lists, because it is configuration rather than an operational view. It used to be listed here
 * as well — the same component under a second sidebar entry — which is two places to look for
 * one thing however much code they share. One destination.
 *
 * ── The role gate is here, and also inside each screen ────────────────────────────────────
 *
 * Deliberately both. This shell decides what an employee can reach; each admin screen re-checks
 * whether the viewer may act. Neither is the real boundary — the Lambda re-reads the caller's
 * role from Cognito and the resolvers sit behind the user pool — but a page that renders an
 * admin screen to an employee and then fails every query is a worse experience than one that
 * redirects.
 */

import React, { Suspense, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canManageUsers, canViewTeamExpenses } from '../../utils/rolePermissions';
import './attendance.css';

const MyAttendance = React.lazy(() => import('./MyAttendance'));
const AdminAttendance = React.lazy(() => import('./AdminAttendance'));
const Regularization = React.lazy(() => import('./Regularization'));
const RawPunches = React.lazy(() => import('./RawPunches'));
const DeviceSync = React.lazy(() => import('./DeviceSync'));
const AttendanceReports = React.lazy(() => import('./AttendanceReports'));

interface Screen {
  id: string;
  title: string;
  description: string;
  adminOnly?: boolean;
  /**
   * Reachable by a manager as well as an admin, showing only that manager's own team.
   *
   * Separate from `adminOnly` because the two answer different questions: `adminOnly` is "may
   * this person operate the system", this is "may this person see other people". A manager may
   * do the second and not the first, which is why Device Sync and Raw Punches stay admin-only.
   */
  teamOrAdmin?: boolean;
  /** Shown instead of `title` to a manager, for whom "Admin" is the wrong word. */
  managerTitle?: string;
  managerDescription?: string;
}

/*
 * There is deliberately no "Manual Attendance" screen.
 *
 * Signing in and signing out is one button pressed twice a day, and the employee already meets
 * it on the home page — the screen they land on at the start of the day, where the card shows
 * their times as they accumulate. A page of its own would be a second place to do the one thing,
 * reachable only by navigating away from the page that already offers it.
 *
 * What lives here instead is everything you would go LOOKING for: the month, the calendar, and
 * the detail for a chosen day. See AttendanceSummaryWidget for the sign-in card.
 */
const SCREENS: Screen[] = [
  {
    id: 'my-attendance',
    title: 'My Attendance',
    description: 'Your attendance, as recorded by the biometric device and your roster.',
  },
  {
    id: 'admin',
    title: 'Admin Attendance',
    description: 'Monitor employee attendance, device punches and manual attendance.',
    teamOrAdmin: true,
    managerTitle: 'Team Attendance',
    managerDescription: "Attendance for the people who report to you, as recorded by the device and their roster.",
  },
  {
    /*
     * Not gated at all.
     *
     * Everybody has days that can go wrong, so everybody can apply. What differs is what the
     * page SHOWS — an employee gets Apply / Pending / Approved, and a manager gets an Approvals
     * tab as well. Gating the route on a role would have hidden the feature from exactly the
     * people it exists for.
     */
    id: 'regularization',
    title: 'Regularization',
    description:
      'Ask for a day to be put right. Your attendance changes only when your manager approves.',
  },
  {
    id: 'raw-punches',
    title: 'Raw Punches',
    description: 'Exactly what the device reported, as it was received.',
    adminOnly: true,
  },
  {
    id: 'device-sync',
    title: 'Device Sync',
    description: 'Connection status, sync history, and pulling punches from the device.',
    adminOnly: true,
  },
  {
    id: 'reports',
    title: 'Attendance Reports',
    description: 'Daily, monthly and exception reports over the attendance on record.',
    adminOnly: true,
  },
];

const AttendancePage: React.FC = () => {
  const { user } = useAuth();
  const { tab } = useParams<{ tab?: string }>();

  const isAdmin = Boolean(user?.role && canManageUsers(user.role));
  const isManager = !isAdmin && Boolean(user?.role && canViewTeamExpenses(user.role));
  const screen = useMemo(() => SCREENS.find((s) => s.id === tab), [tab]);

  /*
   * An employee reaching an admin URL is sent to their own attendance rather than shown an empty
   * page. An unknown one — including the retired /attendance/manual-attendance — lands there too.
   */
  if (!screen) return <Navigate to="/attendance/my-attendance" replace />;
  if (screen.adminOnly && !isAdmin) return <Navigate to="/attendance/my-attendance" replace />;
  if (screen.teamOrAdmin && !isAdmin && !isManager) {
    return <Navigate to="/attendance/my-attendance" replace />;
  }

  const showManagerCopy = isManager && screen.teamOrAdmin;
  const title = (showManagerCopy && screen.managerTitle) || screen.title;
  const description = (showManagerCopy && screen.managerDescription) || screen.description;

  return (
    <section className="att att-page">
      <header className="att-page__head">
        <h1 className="att-page__title">{title}</h1>
        <p className="att-page__sub">{description}</p>
      </header>

      <Suspense fallback={<div className="att-empty">Loading…</div>}>
        {screen.id === 'my-attendance' && <MyAttendance />}
        {screen.id === 'admin' && <AdminAttendance />}
        {screen.id === 'regularization' && <Regularization />}
        {screen.id === 'raw-punches' && <RawPunches />}
        {screen.id === 'device-sync' && <DeviceSync />}
        {screen.id === 'reports' && <AttendanceReports />}
      </Suspense>
    </section>
  );
};

export default AttendancePage;
